import { ChevronLeftIcon, ChevronRightIcon, MoreHorizontalIcon } from 'lucide-react';
import { Button } from './Button';
import { Text } from './Text';
import clsx from 'clsx';

interface PaginationProps {
  className?: string;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  disabled?: boolean;
}

export function Pagination({
  className,
  currentPage,
  totalPages,
  onPageChange,
  disabled,
}: PaginationProps) {
  const generatePageNumbers = () => {
    const pages: (number | 'ellipsis')[] = [];
    const delta = 2; // Number of pages to show around current page

    if (totalPages <= 7) {
      // If total pages <= 7, show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      // Add ellipsis if needed
      if (currentPage > delta + 2) {
        pages.push('ellipsis');
      }

      // Add pages around current page
      const start = Math.max(2, currentPage - delta);
      const end = Math.min(totalPages - 1, currentPage + delta);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      // Add ellipsis if needed
      if (currentPage < totalPages - delta - 1) {
        pages.push('ellipsis');
      }

      // Always show last page if there's more than 1 page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const pages = generatePageNumbers();

  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  if (totalPages <= 1) {
    return null;
  }

  return (
    <nav
      role="navigation"
      aria-label="pagination"
      className={clsx('mx-auto flex w-full justify-center', className)}
    >
      <ul className="flex flex-row items-center gap-1">
        <li>
          <Button
            variant="secondary"
            size="smIcon"
            onClick={handlePrevious}
            disabled={disabled || currentPage <= 1}
          >
            <ChevronLeftIcon size={20} />
          </Button>
        </li>
        {pages.map((page, index) => (
          <li key={index}>
            {page === 'ellipsis' ? (
              <span className="flex items-center justify-center px-2">
                <MoreHorizontalIcon size={16} />
              </span>
            ) : (
              <Button
                variant={page === currentPage ? 'secondary' : 'ghost'}
                size="smIcon"
                onClick={() => onPageChange(page)}
                disabled={disabled}
              >
                <Text variant="label" strong>
                  {page}
                </Text>
              </Button>
            )}
          </li>
        ))}
        <li>
          <Button
            variant="secondary"
            size="smIcon"
            onClick={handleNext}
            disabled={disabled || currentPage >= totalPages}
          >
            <ChevronRightIcon size={20} />
          </Button>
        </li>
      </ul>
    </nav>
  );
}
